<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faCheck, faCopy, faLink, faSignature, faXmark } from '@fortawesome/free-solid-svg-icons'
    import type { DynamicTableColumn, DynamicTableRow, DynamicTableSignatureMeta } from '../../types/dynamic-table'
    import { isRowLocked } from '../../types/dynamic-table'
    import DynamicTableSignPad from './dynamic-table-sign-pad.svelte'
    import { formatDateTime } from '../../utils/format-datetime'

    export let formId: string | null = null
    export let blockId: string
    export let row: DynamicTableRow
    export let column: DynamicTableColumn
    export let disabled = false

    const dispatch = createEventDispatcher<{
        rowUpdated: { content: Record<string, unknown>; updatedAt: string; rowId: string; columnId: string }
    }>()

    let generating = false
    let generatedUrl: string | null = null
    let copied = false
    let signing = false
    let signPadOpen = false
    let signPadDataUrl: string | null = null
    let showDetails = false
    let actionError = ''

    $: cellValue = row.cells[column.id] as DynamicTableSignatureMeta | null
    $: mySignature = cellValue && typeof cellValue === 'object' ? (cellValue as DynamicTableSignatureMeta) : null
    $: rowLocked = isRowLocked(row)
    $: canManageSignature = !!formId && !disabled && !rowLocked

    async function generateLink() {
        if (!formId) return
        generating = true
        actionError = ''
        try {
            const { token } = await window.api.tools.formsV2.forms.dynamicTableCreateRowSignLink(formId, {
                blockId,
                rowId: row.id,
                columnId: column.id,
            })
            generatedUrl = `${window.api.utils.utils.domain()}/public/forms-v2/sign-row?t=${token}`
        } catch {
            actionError = 'Não foi possível gerar o link. Tente novamente.'
        } finally {
            generating = false
        }
    }

    async function copyLink() {
        if (!generatedUrl) return
        try {
            await navigator.clipboard.writeText(generatedUrl)
            copied = true
            setTimeout(() => (copied = false), 1500)
        } catch {
            // clipboard indisponível
        }
    }

    function openSignPad() {
        signPadOpen = true
        signPadDataUrl = null
        actionError = ''
    }

    function closeSignPad() {
        signPadOpen = false
        signPadDataUrl = null
    }

    async function confirmSignature() {
        if (!formId || !signPadDataUrl) return
        signing = true
        actionError = ''
        try {
            const { content, updatedAt } = await window.api.tools.formsV2.forms.dynamicTableSignRowInPerson(formId, {
                blockId,
                rowId: row.id,
                columnId: column.id,
                imageDataUrl: signPadDataUrl,
            })
            signPadOpen = false
            signPadDataUrl = null
            dispatch('rowUpdated', { content, updatedAt, rowId: row.id, columnId: column.id })
        } catch {
            actionError = 'Não foi possível registrar a assinatura. Tente novamente.'
        } finally {
            signing = false
        }
    }

    // Usa o fuso do consultório (window.store.company.timezone) e não o da máquina, para o carimbo
    // exibido aqui bater com o que sai no PDF assinado. Ver utils/format-datetime.ts.
    function formatSignedAt(iso: string): string {
        return formatDateTime(iso)
    }
</script>

<div class="signature-cell">
    {#if mySignature}
        <button type="button" class="signature-badge signed" on:click={() => (showDetails = true)}>
            <Fa icon={faSignature} size="xs" />
            <span>Assinatura</span>
        </button>
    {:else if rowLocked}
        <span class="signature-badge locked">Travado</span>
    {:else if canManageSignature}
        {#if signPadOpen}
            <div class="sign-pad-wrap">
                <DynamicTableSignPad on:change={(e) => (signPadDataUrl = e.detail)} />
                <div class="sign-pad-actions">
                    <button type="button" class="btn-tiny primary" on:click={confirmSignature} disabled={!signPadDataUrl || signing}>
                        {signing ? 'Enviando...' : 'Confirmar assinatura'}
                    </button>
                    <button type="button" class="btn-tiny" on:click={closeSignPad} disabled={signing}>Cancelar</button>
                </div>
            </div>
        {:else if generatedUrl}
            <div class="link-row">
                <input type="text" class="link-input" readonly value={generatedUrl} />
                <button type="button" class="copy-btn" on:click={copyLink} title="Copiar link">
                    <Fa icon={copied ? faCheck : faCopy} size="xs" />
                </button>
            </div>
        {:else}
            <div class="signature-actions">
                <button type="button" class="btn-tiny" on:click={openSignPad}>
                    <Fa icon={faSignature} size="xs" />
                    <span>Assinar agora</span>
                </button>
                <button type="button" class="btn-tiny" on:click={generateLink} disabled={generating}>
                    <Fa icon={faLink} size="xs" />
                    <span>{generating ? 'Gerando...' : 'Gerar link'}</span>
                </button>
            </div>
        {/if}
        {#if actionError}
            <p class="cell-error">{actionError}</p>
        {/if}
    {:else}
        <span class="signature-badge pending">Pendente</span>
    {/if}
</div>

{#if showDetails && mySignature}
    <div class="details-backdrop">
        <div class="details-modal">
            <div class="details-header">
                <h3>Assinatura</h3>
                <button type="button" class="close-btn" on:click={() => (showDetails = false)}>
                    <Fa icon={faXmark} />
                </button>
            </div>
            <img src={mySignature.imageUrl} alt="Assinatura" class="signature-image" />
            <dl class="details-list">
                <dt>Assinado em</dt>
                <dd>{formatSignedAt(mySignature.signedAt)}</dd>
                <dt>IP</dt>
                <dd>{mySignature.ipAddress ?? '—'}</dd>
                <dt>Dispositivo</dt>
                <dd>{mySignature.userAgent ?? '—'}</dd>
            </dl>
        </div>
    </div>
{/if}

<style>
    .signature-cell {
        min-width: 160px;
    }
    .signature-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 999px;
        padding: 4px 12px;
        font-size: 0.78rem;
        font-weight: 500;
        border: none;
        cursor: default;
    }
    .signature-badge.signed {
        background: #ebfbee;
        color: #2b8a3e;
        cursor: pointer;
    }
    .signature-badge.pending {
        background: #f1f3f5;
        color: #868e96;
    }
    .signature-badge.locked {
        background: #f1f3f5;
        color: #adb5bd;
    }
    .signature-actions {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
    }
    .btn-tiny {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 0.75rem;
        font-weight: 500;
        background: #fff;
        color: #495057;
        cursor: pointer;
        white-space: nowrap;
    }
    .btn-tiny:hover:not(:disabled) {
        background: #f8f9fa;
    }
    .btn-tiny:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    .btn-tiny.primary {
        background: var(--cs2-accent, #1bdf6f);
        border-color: var(--cs2-accent, #1bdf6f);
        color: #1f2937;
    }
    .btn-tiny.primary:hover:not(:disabled) {
        background: var(--cs2-accent-hover, #17be5e);
    }
    .sign-pad-wrap {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .sign-pad-actions {
        display: flex;
        gap: 6px;
    }
    .link-row {
        display: flex;
        gap: 6px;
        min-width: 220px;
    }
    .link-input {
        flex: 1;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 5px 8px;
        font-size: 0.75rem;
        color: #495057;
        font-family: 'SFMono-Regular', Consolas, monospace;
    }
    .copy-btn {
        flex-shrink: 0;
        border: 1px solid #dee2e6;
        background: #fff;
        color: #495057;
        border-radius: 6px;
        padding: 0 10px;
        cursor: pointer;
    }
    .copy-btn:hover {
        border-color: var(--cs2-accent, #1bdf6f);
        color: var(--cs2-accent, #1bdf6f);
    }
    .cell-error {
        margin: 4px 0 0;
        font-size: 0.72rem;
        color: #c92a2a;
    }
    .details-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1300;
        background: rgba(17, 19, 24, 0.65);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }
    .details-modal {
        background: #fff;
        border-radius: 14px;
        width: 100%;
        max-width: 380px;
        padding: 18px 20px;
    }
    .details-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
    }
    .details-header h3 {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 600;
        color: #212529;
    }
    .close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: #f1f3f5;
        color: #495057;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        cursor: pointer;
    }
    .signature-image {
        display: block;
        width: 100%;
        max-height: 160px;
        object-fit: contain;
        border: 1px solid #ecedf0;
        border-radius: 8px;
        background: #fbfbfc;
        margin-bottom: 12px;
    }
    .details-list {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 4px 10px;
        margin: 0;
        font-size: 0.82rem;
    }
    .details-list dt {
        color: #868e96;
    }
    .details-list dd {
        margin: 0;
        color: #212529;
        word-break: break-word;
    }
</style>
